import type { SignUpFormValues } from '../schemas';
import type { BusinessStructure, BusinessType } from '@paid-ui/enums/business';
import type { CompanyDirectorsStructure } from '@paid-ui/enums/user';
import type { Address } from '@paid-ui/schemas/zod/address';

import { useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { useRouter } from 'next/router';
import { useMutation } from '@tanstack/react-query';

import { useToast } from '@paid-ui/ui/toast';
import { request } from '@paid-ui/utils/axios';

import { SignUpStep } from '../models/enums';
import { formatFormValues } from '../utils/format-form-values';
import { isIndividualSignUp } from '../utils/is-individual-sign-up';

export interface TrustOverallInfo {
  trust: {
    abn?: string;
    name: string;
    provideBuildingServices: boolean;
    address?: Address;
    builderLicenceNumber?: string;
  };
  individualTrustee?: Record<string, unknown>;
  companyTrustee?: {
    acn: string;
    name: string;
    companyDirectorsStructure: CompanyDirectorsStructure;
    address?: Address;
  };
}

export interface BusinessOverallInfo {
  isEmployee: boolean;
  business?: {
    provideBuildingServices: boolean;
    structure: BusinessStructure;
    abn?: string;
    acn?: string;
    name: string;
    companyDirectorsStructure: CompanyDirectorsStructure | null;
    address?: Address;
    builderLicenceNumber?: string;
  };
  trustOverallInfo?: TrustOverallInfo;
}

export interface UserInfo {
  email?: string;
  firstName: string;
  lastName: string;
  mobile: string;
  password: string;
  mainRole?: BusinessType;
  address?: Address;
}

export type SignUpBody = UserInfo & {
  businessOverallInfo?: BusinessOverallInfo;
};

export interface SignUpResponse {
  data: {
    id: string;
  };
}

export type SignInResponse = {
  access_token: string;
} | null;

export const useSignUp = () => {
  const toast = useToast();
  const router = useRouter();
  const searchParams = useSearchParams();

  const runWhenSignInFailed = useCallback(() => {
    toast.success('You have successfully signed up');
    const newSearchParams = new URLSearchParams(globalThis.location.search);
    newSearchParams.delete('step');
    router.replace({
      pathname: '/signin',
      search: newSearchParams.toString(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return useMutation({
    mutationKey: ['SignUp'],
    mutationFn: async (formValues: SignUpFormValues) => {
      const url = isIndividualSignUp(formValues)
        ? `/users/signup/individual`
        : `/users/signup/business`;

      const { oneTimeCode } = formValues;
      const { data } = await request.post<SignUpResponse>(url, formatFormValues(formValues), {
        params: {
          oneTimeCode,
        },
      });

      return data;
    },
    async onSuccess(_, { email, password }) {
      try {
        const { data } = await request.post<SignInResponse>('/auth/signin', {
          account: email,
          password,
        });

        if (data?.access_token) {
          sessionStorage.setItem('access_token', data.access_token);
          router.replace({
            pathname: '/signup',
            query: {
              ...Object.fromEntries(searchParams.entries()),
              step: SignUpStep.CREATE_CONTRACT,
            },
          });
        } else {
          runWhenSignInFailed();
        }
      } catch {
        runWhenSignInFailed();
      }
    },
    onError(err) {
      toast.error(err instanceof Error ? err.message : 'Failed to sign up');
    },
  });
};
